import { type MouseEvent, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { navigateWithOptionalHash } from '@/utils/navigation';
import { BASE_HOST, DOOPEY_HOST, isBaseHost, isJoLunoHost } from '@/utils/siteHosts';

const LIBRARY_HREF = '/#library';

type BackToLibraryLinkProps = {
  className?: string;
  children?: ReactNode;
  /** Defaults to `/#library`. Use `/` for the same home return as other flagship pages. */
  href?: string;
};

export function BackToLibraryLink({ className, children, href }: BackToLibraryLinkProps) {
  const navigate = useNavigate();
  const target = href ?? LIBRARY_HREF;
  const content = children ?? (isJoLunoHost() ? '← joleu.ng' : "← Doopey's World");

  /* The split made this host-aware:
     - joluno.joleu.ng → the personal directory at joleu.ng (matches the label)
     - joleu.ng (legacy tool/share URLs) → the library's new home on doopey
     - doopey.joleu.ng and local dev → same-origin SPA navigation as before */
  if (isJoLunoHost()) {
    return (
      <a className={className} href={`https://${BASE_HOST}/`}>
        {content}
      </a>
    );
  }
  if (isBaseHost()) {
    return (
      <a className={className} href={`https://${DOOPEY_HOST}${target}`}>
        {content}
      </a>
    );
  }

  const onClick = (e: MouseEvent<HTMLAnchorElement>) => {
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    navigateWithOptionalHash(navigate, target);
  };

  return (
    <Link className={className} to={target} onClick={onClick}>
      {content}
    </Link>
  );
}
